import React, { lazy, useEffect } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { themeChange } from "theme-change";
import initializeApp from "./app/init";

// Unified Mock Test Components (used for all test types: mock, mini-mock, class-test)
import SingleMockTest from "./features/mocktests/components/SingleMockTest";
import SpeakingSection from "./features/mocktests/components/SpeakingSection";
import ListeningPage from "./features/mocktests/components/ListeningPage";
import ReadingPage from "./features/mocktests/components/ReadingPage";
import WritingPage from "./features/mocktests/components/WritingPage";
import SpeakingPage from "./features/mocktests/components/SpeakingPage";

// Test type constants
import { TEST_TYPES } from "./features/mocktests/mockTestSlice";

// Importing pages
const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Register = lazy(() => import("./pages/Register"));
const Documentation = lazy(() => import("./pages/Documentation"));

// Initializing different libraries
initializeApp();

// Auth Guard Component
function AuthGuard({ children }) {
  const location = useLocation();
  const publicRoutes = [
    "/login",
    "/forgot-password",
    "/register",
    "/documentation",
  ];

  // Check if current route is public
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Get token from localStorage
  const token = localStorage.getItem("token");

  // If it's a public route, render the children
  if (isPublicRoute) {
    return children;
  }

  // If not a public route and no token, redirect to login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, render children
  return children;
}

function App() {
  useEffect(() => {
    // 👆 daisy UI themes initialization
    themeChange(false);

    // 🔑 Handle cross-domain authentication token from URL
    const { extractTokenFromUrl } = require('./utils/crossDomainAuth');
    const tokenFound = extractTokenFromUrl();
    
    if (tokenFound) {
      // Force a refresh to apply the new token and trigger AuthGuard
      window.location.href = '/app/dashboard';
    }
  }, []);

  return (
    <>
      <Router>
        <AuthGuard>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/documentation" element={<Documentation />} />

            {/* Place new routes over this */}
            <Route path="/app/*" element={<Layout />} />

            <Route
              path="*"
              element={
                <Navigate
                  to={
                    localStorage.getItem("token") ? "/app/dashboard" : "/login"
                  }
                  replace
                />
              }
            />

            <Route path="/mock-test/:testId" element={<SingleMockTest testType={TEST_TYPES.MOCK_TEST} />} />

            <Route
              path="/mock-speaking/:testId"
              element={<SpeakingSection />}
            />

            <Route path="/mock-test/:testId" element={<SingleMockTest testType={TEST_TYPES.MOCK_TEST} />} />
            <Route
              path="/mock-test/:testId/listening"
              element={<ListeningPage />}
            />
            <Route
              path="/mock-test/:testId/reading"
              element={<ReadingPage />}
            />
            <Route
              path="/mock-test/:testId/writing"
              element={<WritingPage />}
            />
            <Route
              path="/mock-test/:testId/speaking"
              element={<SpeakingPage />}
            />

            {/* Bundle Test Routes */}
            <Route
              path="/bundle-test/:testId/listening"
              element={<ListeningPage isBundle={true} />}
            />
            <Route
              path="/bundle-test/:testId/reading"
              element={<ReadingPage isBundle={true} />}
            />
            <Route
              path="/bundle-test/:testId/writing"
              element={<WritingPage isBundle={true} />}
            />
            <Route
              path="/bundle-test/:testId/speaking"
              element={<SpeakingPage isBundle={true} />}
            />

            {/* Mini Mock Test Routes - Uses unified components */}
            <Route
              path="/mini-mock-test/:testId"
              element={<SingleMockTest testType={TEST_TYPES.MINI_MOCK} />}
            />
            <Route
              path="/mini-mock-test/:testId/listening"
              element={<ListeningPage />}
            />
            <Route
              path="/mini-mock-test/:testId/reading"
              element={<ReadingPage />}
            />
            <Route
              path="/mini-mock-test/:testId/writing"
              element={<WritingPage />}
            />
            <Route
              path="/mini-mock-test/:testId/speaking"
              element={<SpeakingPage />}
            />

            {/* Class Test Routes - Uses unified components */}
            <Route path="/class-test/:testId" element={<SingleMockTest testType={TEST_TYPES.CLASS_TEST} />} />
            <Route
              path="/class-test/:testId/listening"
              element={<ListeningPage />}
            />
            <Route
              path="/class-test/:testId/reading"
              element={<ReadingPage />}
            />
            <Route
              path="/class-test/:testId/writing"
              element={<WritingPage />}
            />
            <Route
              path="/class-test/:testId/speaking"
              element={<SpeakingPage />}
            />

            {/* Bundle Test Routes - reuse mock test components with bundle mode */}
            <Route
              path="/bundle-test/:testId/listening"
              element={<ListeningPage isBundle={true} />}
            />
            <Route
              path="/bundle-test/:testId/reading"
              element={<ReadingPage isBundle={true} />}
            />
            <Route
              path="/bundle-test/:testId/writing"
              element={<WritingPage isBundle={true} />}
            />
            <Route
              path="/bundle-test/:testId/speaking"
              element={<SpeakingPage isBundle={true} />}
            />
          </Routes>
        </AuthGuard>
      </Router>
    </>
  );
}

export default App;
